import { useQmrState } from '@packages/contexts/qmrs';
import { colors } from '@packages/core/styles';
import { CvtExceptionStatus, DsqmReviewStatusId } from '@packages/models/api';
import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    column: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 8,
    },
    container: {
        display: 'flex',
        flexDirection: 'row',
    },
    divider: {
        border: 'none',
        borderTop: `1px solid ${colors.grayThree}`,
        margin: 0,
    },
});

/**
 * The attached test's original CVT repair recommendation and the DSQM's authorized result.
 *
 * Renders the original repair recommendation and adds the DSQM's result once a challenged NQR is approved or denied.
 */
export function CvtRepairResults() {
    const { t } = useTranslation();
    const { qmr } = useQmrState();
    const classes = useStyles();

    const originalRepairRecommendation = qmr?.cvtNorthStarPostResult?.sbrResult?.result;

    const isRecommendationChallengeSubmitted =
        qmr?.cvtChallengeDetails?.cvtExceptionStatus === CvtExceptionStatus.RecommendationChallenged;
    const isDsqmReviewDecided =
        qmr?.dsqmReviewStatus.dsqmReviewStatusId === DsqmReviewStatusId.Approved ||
        qmr?.dsqmReviewStatus.dsqmReviewStatusId === DsqmReviewStatusId.Denied;
    const showDsqmAuthorizedResult = isRecommendationChallengeSubmitted && isDsqmReviewDecided;

    const dsqmAuthorizedResult = qmr?.cvtNorthStarPostResult?.dsqmAuthResult?.result;
    const isRepairAuthorized =
        qmr?.dsqmReviewStatus.dsqmReviewStatusId === DsqmReviewStatusId.Approved && Boolean(dsqmAuthorizedResult);

    if (!originalRepairRecommendation) {
        return null;
    }

    return (
        <div className="d-flex flex-column gap-4">
            <div className={classes.container}>
                <div className={classes.column}>
                    <Typography variant="label">
                        {t(
                            'qmr:sections.failCode.cvtDiagnostic.originalRepairRecommendation',
                            'Original CVT Repair Recommendation'
                        )}
                    </Typography>
                    <Typography>{originalRepairRecommendation}</Typography>
                </div>
                {showDsqmAuthorizedResult && (
                    <div className={classes.column}>
                        <Typography variant="label">
                            {t('qmr:sections.failCode.cvtDiagnostic.dsqmAuthorizedResult', 'DSQM Authorized Result')}
                        </Typography>
                        <Typography>
                            {isRepairAuthorized ? dsqmAuthorizedResult : t('text:notAvailable', 'N/A')}
                        </Typography>
                    </div>
                )}
            </div>
            {isRecommendationChallengeSubmitted && <hr className={classes.divider} />}
        </div>
    );
}
